import { useQuery } from '@tanstack/react-query';
import { produce } from 'immer';
import { DATA_QUERY_PLUGIN_KEY, Notice, NoticeKind, UnknownDefinition, VariableStateMap } from '../../../core';
import { DataQueryPlugin, EventQueryPlugin, QueryPluginContext, VariablePlugin } from '../../model';
import { usePluginQueryContext } from '../common';
import {
  DataQuery,
  getInjectGlobalFiltersFunction,
  getInterpolateVariablesFunction,
  initializeDataQueryPlugin,
} from '../DataQueryPlugins';
import { INTERNAL_GLOBAL_FILTER } from '../GlobalFilters/global-filter-model';
import { usePluginRegistry } from '../plugin-registry';
import { UseDataQueryOptions } from './model';

type ProcessPluginDefinition = {
  definition: UnknownDefinition;
  context: QueryPluginContext;
  plugin: DataQueryPlugin | DataQuery | VariablePlugin | EventQueryPlugin;
};

/**
 * This function processes a plugin definition by interpolating variables and injecting global filters.
 */
export async function processPluginDefinition(args: ProcessPluginDefinition) {
  const { definition, context, plugin } = args;

  const allNotices: Notice[] = [];
  let nextDefinition = definition;

  const interpolateVariables = getInterpolateVariablesFunction(plugin);
  const injectGlobalFilters = getInjectGlobalFiltersFunction(plugin);

  if (interpolateVariables) {
    nextDefinition = produce(nextDefinition, (draft) => {
      draft.spec = interpolateVariables(draft.spec, context);
    });
  }
  if (injectGlobalFilters) {
    try {
      context.globalFilters = context.globalFilters?.filter(
        ({ label, status }) =>
          !context.disabledGlobalFilters?.[(nextDefinition as unknown as { id: string })?.id]?.includes(label) &&
          status !== 'disabled'
      );
      const { spec: injectedSpec, notices = [] } = await injectGlobalFilters(nextDefinition.spec, context);
      nextDefinition = produce(nextDefinition, (draft) => {
        draft.spec = injectedSpec;
      });

      allNotices.push(...notices);
    } catch (e) {
      allNotices.push({
        type: 'error',
        message: `Global filters could not be injected: ${(e as Error).message}`,
        kind: NoticeKind.GlobalFilters,
      });
    }
  } else {
    // If the the global filter is added by a user and the plugin does not support global filters, add a notice
    if (
      context.globalFilters?.length &&
      !context.globalFilters.every((f) => f.statusReason === INTERNAL_GLOBAL_FILTER)
    ) {
      allNotices.push({
        type: 'warning',
        message: `The datasource does not support global filters.`,
        kind: NoticeKind.GlobalFilters,
      });
    }
  }

  return {
    originalDefinition: definition,
    definition: nextDefinition,
    notices: allNotices,
  };
}

/**
 * Helper hook to process multiple data query definitions.
 */
export function useExperimentalProcessQueryDefinitions(
  definitions: UnknownDefinition[],
  options?: UseDataQueryOptions
) {
  const queryContext = usePluginQueryContext();
  const { variableState, globalFilters } = queryContext;
  const { getPlugin } = usePluginRegistry();
  const variableStateValues = getVariableValuesKey(variableState);

  const results = useQuery(['DataQueryProcess', definitions, globalFilters, variableStateValues, options], () => {
    return Promise.all(
      definitions.map(async (definition) => {
        const plugin = (await getPlugin(DATA_QUERY_PLUGIN_KEY, definition.kind)) as DataQueryPlugin;
        const dataQueryPlugin = initializeDataQueryPlugin(plugin);
        const pluginDefinition = await processPluginDefinition({
          definition: definition,
          context: { ...queryContext, ...options },
          plugin: dataQueryPlugin,
        });

        return pluginDefinition;
      })
    );
  });

  return results;
}

/**
 * Returns a serialized string of the current state of variable values.
 */
function getVariableValuesKey(v: VariableStateMap) {
  return Object.values(v)
    .map((v) => JSON.stringify(v.value))
    .join(',');
}
